import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import ApiService from "./../../services/ApiService";
import { useState, useEffect } from "react";
import { validate } from "./../../services/ValidationService";
import {
  goBack,
  convertDOBtoAge,
  convertDOBtoMonth,
  formatMeasurement,
  // convertDOBtoAgerangeId,
  convertS3privateToPublic,
  generateRandomId,
  calculateAgeInMonths,
  getFilteredAgeRange,
} from "./../../services/UtilsService";
import constantVariables from "./../../dataset/constantVariables.json";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import ViewDevelopmentImage from "../modals/ViewDevelopmentImage";
import { useTranslation } from 'react-i18next';

const AddDevelopment = () => {
  //Initial state declaration
  const [modal, setModal] = useState({});
  const [memberData, setMemberData] = useState({});
  const masterDevelopment = useSelector((state) => state?.development);
  const [data, setData] = useState({});
  const [developmentData, setDevelopmentData] = useState([]);
  const [ageRangeArr, setAgeRangeArr] = useState([]);
  const [developmentQuestion, setDevelopmentQuestion] = useState([]);
  const [containsAllRequiredAges, setContainsAllRequiredAges] = useState(false);
  const [ageRanges, setAgeRanges] = useState([]);
  const [nextDevelopment, setNextDevelopment] = useState(null);
  const [error, setError] = useState({});
  const [questionErrors, setQuestionErrors] = useState({});
  const [parentData, setParentData] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  let { id } = useParams();
	const role = Number(localStorage.getItem('role'));
  const { t } = useTranslation();

  /**
   * Validation schema for Add Diagnosis Fields
   * @patient_name - required field and pass the diagnosis name
   * @age_range - required field and pass the age_range
   * @development_question - required field and pass the medication details
   **/
  const Fields = [
    {
      name: "age_range",
      validation: Yup.string().required(constantVariables.error.required),
    },
  ];

  const getQuestionAnswer = (questionId) => {
    return answers.find((answer) => answer.id === questionId)?.value || "";
  };

  const handleRadioChange = async (e, questionId, questionAge) => {
    const { value } = e.target;
    setAnswers((prevAnswers) => {
      const index = prevAnswers.findIndex((answer) => answer.id === questionId);
      if (index !== -1) {
        const updatedAnswers = [...prevAnswers];
        updatedAnswers[index] = { id: questionId, age: questionAge, value };
        return updatedAnswers;
      } else {
        return [...prevAnswers, { id: questionId, age: questionAge, value }];
      }
    });

    const Errors = await validate({ [questionId]: value }, Fields);
    setQuestionErrors({ ...questionErrors, [questionId]: Errors[questionId] }); // Update error state for the question
  };

  //Component Initialize function trigger when request id change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await ApiService.getMemberProfile(id);
        if (response.status === 200) {
          const ageInMonths = calculateAgeInMonths(response.data?.item?.dob);
          const filteredRanges = getFilteredAgeRange(ageInMonths, t);
          setAgeRanges(filteredRanges);
          setMemberData({
            ...response.data.item,
            dobs: convertDOBtoMonth(response.data?.item?.dob, t),
          });
          setParentData(response.data.parentInfo);
          sessionStorage.setItem("parent", response.data.item.parent);

          // Fetch patient development data
          const developmentResponse = await ApiService.getMemberDevelopment({
            id: response.data.item.id,
          });
          if (developmentResponse.status === 200) {
            const developmentItems = developmentResponse?.data?.items;
            setDevelopmentData(developmentItems);

            // Call a function to find and set the initial age_range value
            const initialAgeRange = setInitialAgeRange(
              filteredRanges,
              developmentItems
            );
            if (initialAgeRange !== undefined) {
              const userId = localStorage.getItem("userId");
              const newId = generateRandomId();
              const currentDate = new Date().toISOString();
              setNextDevelopment(initialAgeRange); // Set the initial age range value if found
              setData({
                ...data,
                age_range: initialAgeRange,
                createdAt: currentDate,
                created_by: userId,
                id: newId,
                member: id,
                questions: answers,
                status: memberData?.status,
                updatedAt: currentDate,
                updated_by: null,
                parentId: parentData.account,
              });
              
              let noValueQuestionsId = [];
              //const filteredItems = developmentItems.filter(item => parseInt(item.age_range) < initialAgeRange); // Filter items where age_range < initialAgeRange
              const questionTracker = {}; // Use a plain variable for tracking question states
              developmentItems.forEach(item => { // Iterate through filtered items to find questions with "No" values
                item.questions.forEach(question => {
                  // If question value is "No", update the tracker
                  if (question.value === 'No') {
                    questionTracker[question.id] = 'No';
                  } else if (question.value === 'Yes') {
                    // If the question is updated to "Yes", override it
                    questionTracker[question.id] = 'Yes';
                  }
                });
              });
              noValueQuestionsId = Object.keys(questionTracker)
                                  .filter(id => questionTracker[id] === 'No')     // Get only the "No" values
                                  .map(id => parseInt(id)); 
    
    
              // Find the greatest age_range in developmentItems array
              const greatestAgeRange = Math?.max(
                ...developmentItems?.map((item) => parseInt(item.age_range))
              );
              // Find the greatest age_range object in the developmentItems array
              const greatestAgeRangeObject = developmentItems?.find(
                (item) => item.age_range === greatestAgeRange.toString()
              );
              // Filter out questions with "No" values from the greatestAgeRangeObject questions.value
              // and questions.age less than initialAgeRange
              const noValueQuestions = greatestAgeRangeObject.questions?.filter(
                (question) =>
                  parseInt(question.age) < initialAgeRange &&
                  question.value.toLowerCase() === "no"
              );
              
              const existingNoValueQuestionIds = (noValueQuestions!==undefined) && noValueQuestions.map((question) => question.id); // Extract the question IDs from noValueQuestions

              const missingQuestionIds = noValueQuestionsId.filter( // Find the missing question IDs (those in noValueQuestionsId but not in existingNoValueQuestionIds)
                (id) => !existingNoValueQuestionIds.includes(id)
              );
              
              missingQuestionIds.forEach((missingId) => { // For each missing ID, find the corresponding question in developmentItems and add it to noValueQuestions
                for (const item of developmentItems) { // Find the question with the matching ID in the filtered developmentItems (age_range < initialAgeRange)
                  if (parseInt(item.age_range) < initialAgeRange) {
                    const missingQuestion = item.questions.find((question) => question.id === missingId);
                    if (missingQuestion && missingQuestion.value.toLowerCase() === "no") {
                      // Add the missing question to noValueQuestions
                      noValueQuestions.push(missingQuestion);
                    }
                  }
                }
              });

              const response = await ApiService.masterDevelopment();
              if (response.status === 200) {
              // Fetch additional questions from masterDevelopment.questions with matching IDs
              const additionalQuestions = response?.data?.questions?.filter(
                (question) =>
                  noValueQuestions.some(
                    (noValueQuestion) => noValueQuestion.id === question.id
                  )
              );
              // Filter questions based on initial age_range value
              const filteredQuestions = response?.data?.questions?.filter(
                (question) => parseInt(question.age_range) === initialAgeRange
              );
              // Add additional questions to the existing filteredQuestions
              const updatedQuestions = [
                ...filteredQuestions,
                ...additionalQuestions,
              ];
              setDevelopmentQuestion(updatedQuestions);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching member profile data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  useEffect(() => {
    // developmentData is already populated with data.
    const ageranges = developmentData.map((item) =>
      parseInt(item.age_range, 10)
    );
    setAgeRangeArr(ageranges);
    // const ageRange = convertDOBtoAgerangeId(memberData.dob);
    // Convert item.age_range_array to a Set to remove duplicates and convert back to an array
    const ageArray = [...new Set(ageranges?.map(Number))];
   /* const requiredAges = Array.from(
      { length: ageRange === 0 ? 10 : ageRange },
      (_, i) => i + 1
    ).slice(0, ageRange === 0 ? 10 : ageRange === 1 ? 1 : ageRange - 1) */;    
    const requiredAges = ageRanges.map(range => range.id);
    const containsAllRequiredAges = requiredAges.every((age) =>
      ageArray.includes(age)
    );
    setContainsAllRequiredAges(containsAllRequiredAges);
  }, [developmentData,ageRanges,memberData.dob]);

  const setInitialAgeRange = (filteredRanges, developmentData) => {
    if (filteredRanges.length > 0 && developmentData.length > 0) {
      const existingAgeRanges = developmentData.map((item) =>
        parseInt(item.age_range)
      );
      for (let i = 0; i < filteredRanges.length; i++) {
        const ageRangeId = parseInt(filteredRanges[i].id);
        if (!existingAgeRanges.includes(ageRangeId)) {
          return ageRangeId; // Return the first missing age_range
        }
      }
    }
    return undefined; // Return undefined if no age_range is found
  };

  /**
   * To handle input change event
   * @e - we get the event details from the Change action
   **/
  const handleChange = async (e) => {
    const { name, value } = e.target;
    if (name === "age_range") {
    const specificAgeRanges = [2,3,4,5,6,7,8,9,10];
		const ageInMonths = parseInt(value);
    console.log("age -> :",ageInMonths);
		const isSpecificAgeRange = specificAgeRanges.includes(ageInMonths);
    console.log("isSpecific -> :",isSpecificAgeRange);
		if(isSpecificAgeRange  && ageInMonths === ageRanges[ageRanges.length-1].id){
			error.age_range_error = constantVariables.error.note;
		}else{
			error.age_range_error='';
		}

      setAnswers([]); // Reset answers
      let noValueQuestionsId = [];
              //const filteredItems = developmentData.filter(item => parseInt(item.age_range) < value); // Filter items where age_range < initialAgeRange
              const questionTracker = {}; // Use a plain variable for tracking question states
              developmentData.forEach(item => { // Iterate through filtered items to find questions with "No" values
                item.questions.forEach(question => {
                  // If question value is "No", update the tracker
                  if (question.value === 'No') {
                    questionTracker[question.id] = 'No';
                  } else if (question.value === 'Yes') {
                    // If the question is updated to "Yes", override it
                    questionTracker[question.id] = 'Yes';
                  }
                });
              });
              noValueQuestionsId = Object.keys(questionTracker)
                                  .filter(id => questionTracker[id] === 'No')     // Get only the "No" values
                                  .map(id => parseInt(id)); 

      // Find the greatest age_range in developmentData array
      const greatestAgeRange = Math.max(
        ...developmentData.map((item) => parseInt(item.age_range))
      );
		
      // Step 1: Find the greatest age_range object in the developmentData array
      const greatestAgeRangeObject = developmentData.find(
        (item) => item.age_range === greatestAgeRange.toString()
      );
      
      // Step 2: Filter out questions with "No" values from the greatestAgeRangeObject questions.value
      // and questions.age less than value
      const noValueQuestions = greatestAgeRangeObject?.questions.filter(
        (question) =>
          parseInt(question.age) < value &&
          question.value.toLowerCase() === "no"
      );

      const existingNoValueQuestionIds = (noValueQuestions!==undefined) && noValueQuestions.map((question) => question.id); // Extract the question IDs from noValueQuestions

      const missingQuestionIds = noValueQuestionsId.filter( // Find the missing question IDs (those in noValueQuestionsId but not in existingNoValueQuestionIds)
        (id) => !existingNoValueQuestionIds.includes(id)
      );
      
      missingQuestionIds.forEach((missingId) => { // For each missing ID, find the corresponding question in developmentItems and add it to noValueQuestions
        for (const item of developmentData) { // Find the question with the matching ID in the filtered developmentItems (age_range < initialAgeRange)
          if (parseInt(item.age_range) < value) {
            const missingQuestion = item.questions.find((question) => question.id === missingId);
            if (missingQuestion && missingQuestion.value.toLowerCase() === "no") {
              // Add the missing question to noValueQuestions
              noValueQuestions.push(missingQuestion);
            }
          }
        }
      });

      // Step 3: Fetch additional questions from masterDevelopment.questions with matching IDs
      const additionalQuestions = masterDevelopment.questions.filter(
        (question) =>
          noValueQuestions?.some(
            (noValueQuestion) => noValueQuestion.id === question.id
          )
      );

      // Step 4: Filter questions based on initial age_range value
      const filteredQuestions = masterDevelopment?.questions.filter(
        (question) => parseInt(question.age_range) == value
      );

      // Add additional questions to the existing filteredQuestions
      const updatedQuestions = [...filteredQuestions, ...additionalQuestions];
      setDevelopmentQuestion(updatedQuestions);
      setNextDevelopment(value);
    } else {
      setData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }

    const Errors = await validate({ [name]: value }, Fields);
    setError({ ...error, [name]: Errors[name] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
	if(error?.age_range_error === '' || error?.age_range_error === undefined){
		if (developmentQuestion.length > 0) {
      // Check if any question is unanswered
      const unansweredQuestions = developmentQuestion.filter(
        (question) => !getQuestionAnswer(question.id)
      );
      if (unansweredQuestions.length === 0) {
        // All questions answered, proceed with form submission
        const userId = localStorage.getItem("userId");
        const newId = generateRandomId();
        const currentDate = new Date().toISOString();

        setData({
          ...data,
          age_range: nextDevelopment,
          createdAt: currentDate,
          created_by: userId,
          id: newId,
          member: id,
          questions: answers,
          status: memberData?.status,
          updatedAt: currentDate,
          updated_by: null,
          parentId: parentData.account,
        });

        try {
          const response = await ApiService.createDevelopment({
            age_range: nextDevelopment,
            createdAt: currentDate,
            created_by: userId,
            id: newId,
            member: id,
            questions: answers,
            status: memberData?.status,
            updatedAt: currentDate,
            updated_by: null,
            parentId: parentData.account,
          });
          if (response.status === 200) {
            toast.success(response?.data?.message);
            if(role===4){
              navigate("/hospital/admin/children");
            }else{
              navigate("/physician/development");
            }
          } else {
            toast.error(response?.data?.message || "Something went wrong");
          }
        } catch (error) {
          console.error("Error:", error);
        }
      } else {
        // Display error for unanswered questions
        unansweredQuestions.forEach((question) => {
          setQuestionErrors((prevErrors) => ({
            ...prevErrors,
            [question.id]: t('error.required'),
          }));
        });
      }
    } else {
      // Check if any question is unanswered
      const questions = masterDevelopment?.questions.filter(
        (question) => question.age_range === 1
      );
      const unansweredQuestions = questions.filter(
        (question) => !getQuestionAnswer(question.id)
      );
      if (unansweredQuestions.length === 0) {
        // All questions answered, proceed with form submission
        const userId = localStorage.getItem("userId");
        const newId = generateRandomId();
        const currentDate = new Date().toISOString();
        setData({
          ...data,
          age_range: 1,
          createdAt: currentDate,
          created_by: userId,
          id: newId,
          member: id,
          questions: answers,
          status: memberData?.status,
          updatedAt: currentDate,
          updated_by: null,
          parentId: parentData.account,
        });

        try {
          const response = await ApiService.createDevelopment({
            age_range: 1,
            createdAt: currentDate,
            created_by: userId,
            id: newId,
            member: id,
            questions: answers,
            status: memberData?.status,
            updatedAt: currentDate,
            updated_by: null,
            parentId: parentData.account,
          });
          if (response.status === 200) {
            toast.success(response?.data?.message);
            if(role===4){
              navigate("/hospital/admin/children");
            }else{
              navigate("/physician/development");
            }
          } else {
            toast.error(response?.data?.message || "Something went wrong");
          }
        } catch (error) {
          console.error("Error:", error);
        }
      } else {
        // Display error for unanswered questions
        unansweredQuestions.forEach((question) => {
          setQuestionErrors((prevErrors) => ({
            ...prevErrors,
            [question.id]: t('error.required'),
          }));
        });
      }
    }
	}
    
  };

  const getCategoryName = (developmentStage) => {
    switch (developmentStage) {
      case 1:
        return t('common.motor');
      case 2:
        return t('common.cognition');
      case 3:
        return t('common.social');
      case 4:
        return t('common.language');
      default:
        return "";
    }
  };

  //To handle the form reset
  const handleCancel = () => {
    setData({});
    /* setError({}); */
    setQuestionErrors({});
    setAnswers([]);
  };

  //callback function
  const handleClose = () => setModal({});

  //Bind the question value
  const getLocalizedQuestion = (question) => {
    const currentLang = localStorage.getItem('i18nextLng');
    if (currentLang === 'ta') {
      return question.question_tamil || question.question; // fallback if tamil not present
    }
    return question.question;
  };

  return (
    <main id="main" className="main">
      {/*ViewDevelopmentImage Modal*/}
      {Object.keys(modal).length !== 0 && modal.status && (
        <ViewDevelopmentImage data={modal} handleClose={handleClose} />
      )}
      {/* Page Title Section */}
      <div className="pagetitle clearfix">
        <div className="float-start">
          <h1>{t('common.addDevelopment')}</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to={role === 4 ? '/hospital/admin/dashboard' : '/physician/dashboard'}>{t('sidebar.home')}</Link>
              </li>
              <li className="breadcrumb-item active">{t('common.addDevelopment')}</li>
            </ol>
          </nav>
        </div>
        <div className="float-end mt-2 me-1">
          <span onClick={goBack} className="backIcon">
            <i className="iconly-Bold-ArrowLeftSquare"></i>
          </span>
        </div>
        {/* End Buttons */}
      </div>
      {/* End Page Title Section */}
      {/* Page Content Section */}
      <section className="pagesection">
        <div className="row">
          <div className="col-xxl-12">
            <form className="form-card p-2">
              {/*Patient(kids) Info*/}
              {!isLoading &&
              <>
              <div className="row">
                <div
                  className={
                    memberData.gender === 0
                      ? "col-lg-12 male"
                      : "col-lg-12 female"
                  }
                >
                  <div className="d-md-flex align-items-center ps-1">
                    <div className="avtar-img">
                      <span className="rounded-circle">
                        {memberData?.photo === "default.png" ? (
                          <img
                            className="img-fluid d-block mx-auto"
                            src="/assets/img/user-avatar.png"
                            alt="UN"
                          />
                        ) : (
                          <img
                            className="img-fluid d-block mx-auto"
                            src={convertS3privateToPublic(memberData?.photo)}
                            alt="UN"
                          />
                        )}
                      </span>
                    </div>
                    <div className="ms-lg-2">
                      <Link to={role === 4 ? '/hospital/admin/view-children/' + memberData?.id : '/physician/member-profile/' + memberData?.id}>
                        <h5 className="table-title fw-bold">
                          {memberData.name ? memberData?.name : "--"}
                        </h5>
                      </Link>
                      <p className={`table-subtitle ${role === 4 ? 'admin-table-subtitle' : ''}`}>
                        <span>{memberData.gender === 0 ? t('common.boy') :  t('common.girl')}</span>{" "}
                        - {convertDOBtoAge(memberData.dob, t)}
                      </p>
                    </div>
                    <div className="ms-lg-5">
                      <p className="mb-0 small">{t('sidebar.parent')}</p>
                      <p className="mb-0 table-subtitle">
                        {parentData.first_name
                          ? parentData?.first_name + " " + parentData?.last_name
                          : "--"}
                      </p>
                    </div>
                    <div className="ms-lg-5">
                      <p className="mb-0 small">{t('table.bloodGroup')}</p>
                      <p className="mb-0 table-subtitle">
                        {memberData.blood_group
                          ? memberData?.blood_group
                          : "--"}
                      </p>
                    </div>
                    <div className="ms-lg-5">
                      <p className="mb-0 small">{t('table.weight')} / {t('table.height')}</p>
                      <p className="mb-0 table-subtitle">
                        {memberData.latest_weight &&
                        memberData.latest_weight !== "0"
                          ? `${formatMeasurement(memberData.latest_weight)} ${t('common.kg')}`
                          : "--"}{" "}
                        /{" "}
                        {memberData?.latest_height &&
                        memberData.latest_height !== "0"
                          ? `${formatMeasurement(memberData.latest_height)} ${t('common.cm')}`
                          : "--"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <hr className="my-3" />
              </>}
              {/*--*/}
              {containsAllRequiredAges && ageRangeArr.length > 0 ? (
                <></>
              ) : (
                !isLoading && <div className="row">
                  <div className="col-lg-4 col-md-6">
                    <div className=" form-group">
                      <label>{t('table.age')}</label>
                      <select
                        name="age_range"
                        value={nextDevelopment}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="" disabled>
                          {t('common.selectAge')}
                        </option>
                        {/* Filter ageRanges based on values not present in developmentData */}
                        {ageRanges &&
                          ageRanges.length > 0 &&
                          ageRanges.map((option) => {
                            // Check if the option already exists in developmentData
                            const existsInDevelopmentData =
                              developmentData.some(
                                (item) => parseInt(item.age_range) === option.id
                              ); 
                            // Render the option only if it doesn't exist in developmentData
                            if (!existsInDevelopmentData) {
                              return (
                                <option key={option.id} value={option.id}>
                                  {option.name}
                                </option>
                              );
                            } else {
                              return null;
                            }
                          })}
                      </select>

                      {error && error.age_range && (
                        <span className="required">{error.age_range}</span>
                      )}
                    </div>
                  </div>
				  <div className="col-lg-8 col-md-6" style={{padding:'1.75rem 1.5rem'}}>
					{error && error.age_range_error && (
						<span className="required-note">{error.age_range_error}</span>
					)}
				  </div>
                </div>
              )}

              {/**/}
              {/* Map over filtered questions */}
              {developmentQuestion && developmentQuestion.length > 0 ? (
      developmentQuestion
        .reduce((acc, question) => {
          if (!acc[question.development_stage]) {
            acc[question.development_stage] = [];
          }
          acc[question.development_stage].push(question);
          return acc;
        }, [])
        .map((questionsGroup, groupIndex) => (
          <React.Fragment key={groupIndex}>
            <div className="row">
              <div className="col-xxl-12">
                <div className={`secTitle ${role === 4 ? 'admin-secTitle' : ''}`}>
                  <h1>{getCategoryName(questionsGroup[0].development_stage)}</h1>
                </div>
              </div>
            </div>
            <div className="row">
              {questionsGroup.map((question, index) => (
                <div key={index} className="col-lg-6 col-md-6">
                  <div className="form-group">
                    <label>{getLocalizedQuestion(question)}</label>
                    {question.image && (
                      <i
                        className="fa-solid fa-image"
                        style={{ color: "#0991FC", marginLeft: "10px", cursor: "pointer" }}
                        onClick={() =>
                          setModal({
                            status: true,
                            title: getCategoryName(questionsGroup[0].development_stage),
                            image: question.image,
                            question: getLocalizedQuestion(question)
                          })
                        }
                      ></i>
                    )}
                    <div className="ChecksRadios developmentRadio" style={{ backgroundColor: "#f0f4f4" }}>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input yes"
                          type="radio"
                          name={`question_${question.id}`}
                          id={`question_yes_${question.id}`}
                          value="Yes"
                          checked={getQuestionAnswer(question.id) === "Yes"}
                          onChange={(e) => handleRadioChange(e, question.id, question.age_range)}
                          style={{ borderColor: getQuestionAnswer(question.id) === "Yes" ? "green" : "" }}
                        />
                        <label className="form-check-label" htmlFor={`question_yes_${question.id}`}>{t('modal.yes')}</label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input no"
                          type="radio"
                          name={`question_${question.id}`}
                          id={`question_no_${question.id}`}
                          value="No"
                          checked={getQuestionAnswer(question.id) === "No"}
                          onChange={(e) => handleRadioChange(e, question.id, question.age_range)}
                          style={{ borderColor: getQuestionAnswer(question.id) === "No" ? "red" : "" }}
                        />
                        <label className="form-check-label" htmlFor={`question_no_${question.id}`}>{t('modal.no')}</label>
                      </div>
                    </div>
                    {questionErrors[question.id] && (
                      <span className="required">{questionErrors[question.id]}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </React.Fragment>
                  ))
              ) : 
              !isLoading && ageRangeArr.length > 0 ? 
              (
                <div
                  className="text-center"
                  style={{
                    fontWeight: "bold",
                    marginTop: "50px",
                    marginBottom: "50px",
                  }}
                >
                  {t('common.noRecord')}
                </div>
              ) : (
                // If developmentQuestion is empty, render questions from masterDevelopment.questions where age_range is 1
                !isLoading &&
                masterDevelopment?.questions
                  .filter((question) => question.age_range === 1)
                  .reduce((acc, question) => {
                    if (!acc[question.development_stage]) {
                      acc[question.development_stage] = [];
                    }
                    acc[question.development_stage].push(question);
                    return acc;
                  }, [])
                  .map((questionsGroup, groupIndex) => (
                    <React.Fragment key={groupIndex}>
                      {/* Render section based on development_stage */}
                      <div className="row">
                        <div className="col-xxl-12">
                          <div className={`secTitle ${role === 4 ? 'admin-secTitle' : ''}`}>
                            {/* Function to get the category name */}
                            <h1>
                              {getCategoryName(
                                questionsGroup[0].development_stage
                              )}
                            </h1>
                          </div>
                        </div>
                      </div>
                      {/* Render questions */}
                      <div className="row">
                        {questionsGroup.map((question, index) => (
                          <div key={index} className="col-lg-6 col-md-6">
                            <div className="form-group">
                              <label>{getLocalizedQuestion(question)}</label>
                              {question.image && (
                      <i
                        className="fa-solid fa-image"
                        style={{ color: "#74C0FC", marginLeft: "10px", cursor: "pointer" }}
                        onClick={() =>
                          setModal({
                            status: true,
                            title: getCategoryName(questionsGroup[0].development_stage),
                            image: question.image,
                            question: getLocalizedQuestion(question)
                          })
                        }
                      ></i>
                    )}
                              <div
                                className="ChecksRadios developmentRadio"
                                style={{ backgroundColor: "#f0f4f4" }}
                              >
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input yes"
                                    type="radio"
                                    name={`question_${question.id}`}
                                    id={`question_yes_${question.id}`}
                                    value="Yes"
                                    checked={
                                      getQuestionAnswer(question.id) === "Yes"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(
                                        e,
                                        question.id,
                                        question.age_range
                                      )
                                    }
                                    style={{
                                      borderColor:
                                        getQuestionAnswer(question.id) === "Yes"
                                          ? "green"
                                          : "",
                                    }}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`question_yes_${question.id}`}
                                  >
                                    {t('modal.yes')}
                                  </label>
                                </div>
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input no"
                                    type="radio"
                                    name={`question_${question.id}`}
                                    id={`question_no_${question.id}`}
                                    value="No"
                                    checked={
                                      getQuestionAnswer(question.id) === "No"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(
                                        e,
                                        question.id,
                                        question.age_range
                                      )
                                    }
                                    style={{
                                      borderColor:
                                        getQuestionAnswer(question.id) === "No"
                                          ? "red"
                                          : "",
                                    }}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`question_no_${question.id}`}
                                  >
                                    {t('modal.no')}
                                  </label>
                                </div>
                              </div>

                              {/* Display error message for this question */}
                              {questionErrors[question.id] && (
                                <span className="required">
                                  {questionErrors[question.id]}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </React.Fragment>
                  ))
              )}

              {/* Button Section */}
              {containsAllRequiredAges && ageRangeArr.length > 0 ? (
                <></>
              ) : (
                <div className="row">
                  <div className="col-lg-12 text-end">
                    <button
                      type="button"
                      className={`btn ${role===4?'btn-tmAquaDgreen':'btn-secondary'} me-2`}
                      onClick={handleCancel}
                    >
                      <i className="ri-arrow-left-line align-middle"></i> {t('common.reset')}
                    </button>
                    <button
                      type="button"
                      className={`btn ${role===4?'btn-tmAquaGreen':'btn-tmBlue'}`}
                      onClick={handleSubmit}
                    >
                      <i className="ri-check-double-fill align-middle"></i>{" "}
                      {t('profile.submit')}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
      {/* End Page Content Section */}
    </main>
  );
};

export default AddDevelopment;
